import DetailLayout from 'ui-component/DetailLayout';
import { useNavigate } from 'react-router-dom';
import KitDetail from 'views/kitting/Kit/components/KitDetail';
import {
  IconCalendarEvent,
  IconListDetails,
  IconShoppingCart,
  IconPackage,
  IconShoppingCartQuestion,
} from '@tabler/icons-react';
import {
  ALL_APP_IDS,
  ALL_APPS,
  ALL_FEATURE_IDS,
  ALL_VIEW_IDS,
  ALL_VIEWS,
} from 'constants/appConstants';
import { ALL_RECORD_TYPE_IDS } from 'constants/recordTypes';
import KitRequestLineTable from 'views/kitting/Kit/KitRequestLineTable';
import { appPermissionAccessLevels } from 'types/apps';
import {
  useDeleteKitRequestsMutation,
  useUpdateKitRequestsMutation,
} from 'store/slices/clientV2/kitRequests';
import SidebarDeleteButtonClientV2 from 'ui-component/DetailLayout/SidebarComponents/SidebarDeleteButtonClientV2';
import { InfoSectionItemFormatTypes } from 'ui-component/InfoSectionItem';
import EditInline from 'ui-component/EditInline';
import {
  HookFormTeamAutocomplete,
  HookFormToggle,
} from 'ui-component/HookFormComponents';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { createUpdateKitRequestFormConstants } from 'views/kitting/components/constants';
import { RelatedRecord, RelatedRecordType } from 'types/inventory';
import RelatedProductionRunDialog from 'ui-component/RelatedProductionRunDialog';
import { useMemo, useState } from 'react';
import {
  KitRequestSubtype,
  useKitRequestContext,
} from 'views/kitting/Kit/KitRequestContext';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import { KitRequestSchema } from 'types/kitting';
import ProgressStack from 'ui-component/ProgressStack';
import { getProgressStackData } from 'views/kitting/Kit/utils';
import _ from 'lodash';
import SidebarCustomProperties from 'ui-component/DetailLayout/SidebarComponents/SidebarCustomProperties';
import { CustomPropertyModel } from 'types/customProperty';
import { ApproveUnapproveKitRequestDialog } from 'views/kitting/Kit/components/ApproveUnapproveKitRequestDialog';
import { canDeleteKitRequest } from 'views/kitting/utils';
import { COFACTR_ORG_ID, COFACTR_SANDBOX_ORG_ID } from 'constants/envConstants';
import RelatedShipmentTab from 'views/kitting/Kit/RelatedShipmentTab';
import { pluralizeNoun } from 'utils/functions';

type RelatedShipmentTabRecord = {
  id: string;
  shipmentCode: string;
  stockLotIds: Set<string>;
  shipmentLines: DecoratedKitRequestLine['shipmentLines'];
};

const Kit = ({ kitRequestId }: { kitRequestId?: KitRequestSchema['id'] }) => {
  const navigate = useNavigate();

  const [openRelatedProductionRunDialog, setOpenRelatedProductionRunDialog] =
    useState(false);

  const { hasAppPermission, hasFeaturePermission } = useAppAccessContext();
  const propertiesAppEnabled = hasAppPermission(ALL_APP_IDS.PROPERTIES);
  const warehouseShipmentsFeatureEnabled = hasFeaturePermission(
    ALL_FEATURE_IDS.SHIP_TO_WAREHOUSE
  );
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();

  const hasSimplifiedBuying = hasAppPermission(
    ALL_APP_IDS.SIMPLIFIED_BUYING_ORDERS
  );

  const {
    kitRequest,
    isLoadingKitRequest,
    isSuccessKitRequest,
    isFetchingKitRequest,
    kitRequestStatuses,
    decoratedKitRequestLines,
    relatedShipments,
    isLoadingShipmentLines,
    isLoadingStockLots,
  } = useKitRequestContext();

  const disableEdit = Boolean(kitRequest?.approved || kitRequest?.locked);

  const shipmentTabs = useMemo(() => {
    if (!decoratedKitRequestLines) return [];

    const tabRecords = decoratedKitRequestLines.reduce((acc, kitLine) => {
      kitLine.shipmentLines
        ?.filter((sl) => sl.shipment.shippedAt)
        .forEach((shipmentLine) => {
          acc[shipmentLine.shipment.id] = {
            id: shipmentLine.shipment.id,
            shipmentCode: shipmentLine.shipment.shipmentCode,
            stockLotIds: (
              acc[shipmentLine.shipment.id]?.stockLotIds ?? new Set()
            ).add(shipmentLine.stockLot?.id ?? ''),
            shipmentLines: (
              acc[shipmentLine.shipment.id]?.shipmentLines ?? []
            ).concat(shipmentLine),
          };
        });
      return acc;
    }, {} as Record<string, RelatedShipmentTabRecord>);

    return Object.values(tabRecords);
  }, [decoratedKitRequestLines]);

  const relatedProductionRun: RelatedRecord = {
    name: kitRequest?.productionRun?.name ?? '',
    recordType: RelatedRecordType.PRODUCTION_RUN,
    recordId: kitRequest?.productionRun?.id ?? '',
    orgId: kitRequest?.org?.id ?? '',
  };

  const relatedSnapshot: RelatedRecord = {
    name: 'Cart',
    recordType: RelatedRecordType.SNAPSHOT,
    recordId: kitRequest?.kittedFor ?? '',
    orgId: kitRequest?.org?.id ?? '',
  };

  const progressStackBarData = useMemo(
    () =>
      getProgressStackData(
        kitRequestId ? kitRequestStatuses?.[kitRequestId] : undefined
      ),
    [kitRequestStatuses, kitRequestId]
  );

  const kitNotFound = useMemo(
    () => isSuccessKitRequest && !isFetchingKitRequest && !kitRequest,
    [isSuccessKitRequest, isFetchingKitRequest, kitRequest]
  );

  const isManagedByCofactr =
    !!kitRequest &&
    _.every(
      kitRequest?.sourceLocations,
      (sl) => sl.org === COFACTR_ORG_ID || sl.org === COFACTR_SANDBOX_ORG_ID
    );

  return (
    <>
      {kitRequestId && (
        <DetailLayout
          recordType={ALL_RECORD_TYPE_IDS.KIT_REQUEST}
          record={kitRequest}
          isLoadingRecord={isLoadingKitRequest}
          headerSection={<KitDetail kitRequestId={kitRequestId} />}
          headerBottomContent={
            kitNotFound ? undefined : (
              <ProgressStack bars={progressStackBarData} />
            )
          }
          relatedDocumentRecords={relatedShipments}
          managedByCofactrApprovedText={
            isManagedByCofactr
              ? 'Sit back and relax! This kit is Managed by Cofactr, so we will pick, prep, pack, and ship these parts from your inventory. If you would like to make changes to this kit, please contact Cofactr Success via the chat in the lower right hand corner of the Cofactr platform. '
              : undefined
          }
          managedByCofactrUnapprovedText={
            isManagedByCofactr
              ? 'This kit is Managed by Cofactr, so we will pick, prep, pack, and ship these parts from your inventory once you click Approve.'
              : undefined
          }
          tabOptions={
            kitNotFound
              ? []
              : [
                  {
                    name: 'lines',
                    label: 'Kit Parts',
                    icon: <IconListDetails />,
                    loading: false,
                    contents: <KitRequestLineTable />,
                    permissionScope: { app: ALL_APPS.KITTING.id },
                    gridPadding: true,
                  },
                  ...(warehouseShipmentsFeatureEnabled &&
                  !isLoadingShipmentLines &&
                  !isLoadingStockLots
                    ? shipmentTabs.map((tab) => ({
                        name: `shipment-${tab.id}`,
                        label: `Shipment ${tab.shipmentCode} (${
                          tab.stockLotIds.size
                        } ${pluralizeNoun('Stock Lot', tab.stockLotIds.size)})`,
                        icon: <IconPackage />,
                        loading: false,
                        contents: (
                          <RelatedShipmentTab
                            shipmentId={tab.id}
                            shipmentLines={tab.shipmentLines}
                            isLoadingShipmentLines={isLoadingShipmentLines}
                          />
                        ),
                        permissionScope: { app: ALL_APPS.KITTING.id },
                        gridPadding: true,
                      }))
                    : []),
                  ...(warehouseShipmentsFeatureEnabled &&
                  (isLoadingShipmentLines || isLoadingStockLots)
                    ? [
                        {
                          name: 'shipment-loading',
                          label: 'Loading Shipments',
                          icon: <IconPackage />,
                          loading: true,
                          contents: <></>,
                          permissionScope: { app: ALL_APPS.KITTING.id },
                          gridPadding: true,
                        },
                      ]
                    : []),
                ]
          }
          sidebarActionButtons={[
            <ApproveUnapproveKitRequestDialog />,
            <SidebarDeleteButtonClientV2
              idToUpdate={kitRequestId}
              recordName="Kit"
              useClientDeleteMutation={useDeleteKitRequestsMutation}
              disabled={kitRequest ? !canDeleteKitRequest(kitRequest) : true}
              onSuccess={() => {
                navigate(ALL_VIEWS[ALL_VIEW_IDS.KITS].path as string);
              }}
            />,
          ]}
          sidebarInfoSections={[
            {
              caption: 'Ship Partial',
              isLoading: isLoadingKitRequest,
              formatType: InfoSectionItemFormatTypes.boolean,
              value: kitRequest?.shipPartial,
              visible: !!kitRequest?.shipTo,
              editInline: (
                <EditInline
                  useMutation={useUpdateKitRequestsMutation}
                  name={createUpdateKitRequestFormConstants.shipPartial.id}
                  hideLabel
                  value={kitRequest?.shipPartial}
                  id={kitRequest?.id || ''}
                  submitAsClientV2Api
                >
                  <HookFormToggle disableRequiredProps />
                </EditInline>
              ),
            },
            {
              caption: handleReplaceWithCustomTeamName('Team'),
              isLoading: isLoadingKitRequest,
              formatType: InfoSectionItemFormatTypes.string,
              value: kitRequest?.team?.name ?? '-',
              permissionScope: {
                app: ALL_APPS.PROPERTIES.id,
                accessLevel: appPermissionAccessLevels.edit,
              },
              disableEdit: Boolean(
                disableEdit ||
                  kitRequest?.productionRun?.team === kitRequest?.team?.id
              ),
              editInline: (
                <EditInline
                  useMutation={useUpdateKitRequestsMutation}
                  name={createUpdateKitRequestFormConstants.team.id}
                  label={handleReplaceWithCustomTeamName(
                    createUpdateKitRequestFormConstants.team.label
                  )}
                  value={kitRequest?.team?.id}
                  id={kitRequestId}
                  submitAsClientV2Api
                  preSubmit={(data) => ({
                    [createUpdateKitRequestFormConstants.team.id]: data ?? null,
                  })}
                >
                  <HookFormTeamAutocomplete isInlineEdit disableRequiredProps />
                </EditInline>
              ),
              visible: propertiesAppEnabled,
            },
            {
              caption: 'Programs',
              isLoading: isLoadingKitRequest,
              formatType: InfoSectionItemFormatTypes.relatedRecords,
              Icon: IconCalendarEvent,
              onOpenEditRelatedRecords: () =>
                setOpenRelatedProductionRunDialog(true),
              count: kitRequest?.productionRun ? 1 : 0,
              relatedRecordsValue: kitRequest?.productionRun
                ? [relatedProductionRun]
                : [],
            },
            ...(hasSimplifiedBuying
              ? [
                  {
                    caption: 'Ordered Cart',
                    isLoading: isLoadingKitRequest,
                    formatType: InfoSectionItemFormatTypes.relatedRecords,
                    Icon: IconShoppingCartQuestion,
                    relatedRecordsValue: kitRequest?.purchaseRequestGroup
                      ? [
                          {
                            name:
                              kitRequest?.purchaseRequestGroup?.name ??
                              kitRequest?.purchaseRequestGroup
                                ?.purchaseRequestGroupCode ??
                              'Order',
                            recordType:
                              RelatedRecordType.CART_PURCHASE_REQUEST_GROUP,
                            recordId:
                              kitRequest?.purchaseRequestGroup?.id ?? '',
                            orgId: kitRequest?.org?.id ?? '',
                          },
                        ]
                      : [],
                    visible: !!kitRequest?.purchaseRequestGroup,
                    count: kitRequest?.purchaseRequestGroup ? 1 : 0,
                  },
                ]
              : [
                  {
                    caption: 'Cart',
                    isLoading: isLoadingKitRequest,
                    formatType: InfoSectionItemFormatTypes.relatedRecords,
                    Icon: IconShoppingCart,
                    relatedRecordsValue: kitRequest?.kittedFor
                      ? [relatedSnapshot]
                      : [],
                    visible: !!kitRequest?.kittedFor,
                  },
                  {
                    caption: 'Requisition',
                    isLoading: isLoadingKitRequest,
                    formatType: InfoSectionItemFormatTypes.relatedRecords,
                    Icon: IconShoppingCartQuestion,
                    relatedRecordsValue: kitRequest?.purchaseRequestGroup
                      ? [
                          {
                            name:
                              kitRequest?.purchaseRequestGroup
                                ?.purchaseRequestGroupCode ??
                              kitRequest?.purchaseRequestGroup?.name ??
                              'Requisition',
                            recordType:
                              RelatedRecordType.PURCHASE_REQUEST_GROUP,
                            recordId:
                              kitRequest?.purchaseRequestGroup?.id ?? '',
                            orgId: kitRequest?.org?.id ?? '',
                          },
                        ]
                      : [],
                    visible: !!kitRequest?.purchaseRequestGroup,
                    count: kitRequest?.purchaseRequestGroup ? 1 : 0,
                  },
                ]),
          ]}
          additionalSidebarSections={[
            ...(kitRequest
              ? [
                  <SidebarCustomProperties<KitRequestSchema, KitRequestSubtype>
                    useMutation={useUpdateKitRequestsMutation}
                    record={kitRequest}
                    model={CustomPropertyModel.KIT_REQUEST}
                    isLoadingRecord={isLoadingKitRequest}
                    disableEdit={disableEdit}
                    idProp="id"
                    submitAsClientV2Api
                  />,
                ]
              : []),
          ]}
        />
      )}
      <RelatedProductionRunDialog
        onClose={() => setOpenRelatedProductionRunDialog(false)}
        open={openRelatedProductionRunDialog}
        relatedProductionRun={relatedProductionRun}
        ids={[kitRequestId ?? '']}
        useUpdateBatchMutation={useUpdateKitRequestsMutation}
      />
    </>
  );
};

export default Kit;
